
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { useToast } from '@/hooks/use-toast';
import { useUpdateTimeSlot } from '@/api/classes';
import type { GymClass, TimeSlot } from '@/api/classes';
import { StaffSelector } from './staff-selector';
import { DaysOfWeekSelector } from './days-of-week-selector';

interface EditTimeSlotDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  gymClass: GymClass;
  timeSlotId: string;
  timeSlot: TimeSlot;
}

interface FormData {
  startTime: string; // HH:mm
  endTime: string; // HH:mm
  maxParticipants: number;
  disabled: boolean;
  date: string; // YYYY-MM-DD (optional - for specific date only)
  daysOfWeek: number[]; // Days of week (all 7 = everyday)
  staffMember: string;
}

export function EditTimeSlotDialog({
  open,
  onOpenChange,
  gymClass,
  timeSlotId,
  timeSlot,
}: EditTimeSlotDialogProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const updateTimeSlotMutation = useUpdateTimeSlot();

  // Determine initial days of week
  const getInitialDaysOfWeek = () => {
    if (timeSlot.daysOfWeek && timeSlot.daysOfWeek.length > 0) {
      return timeSlot.daysOfWeek;
    }
    if (timeSlot.applyEveryDay) {
      return [0, 1, 2, 3, 4, 5, 6]; // All days
    }
    return [0, 1, 2, 3, 4, 5, 6]; // Default to all days
  };

  const { register, handleSubmit, setValue, watch } = useForm<FormData>({
    defaultValues: {
      startTime: timeSlot.startTime,
      endTime: timeSlot.endTime,
      maxParticipants: timeSlot.maxParticipants,
      disabled: timeSlot.disabled || false,
      date: timeSlot.date || '',
      daysOfWeek: getInitialDaysOfWeek(),
      staffMember: timeSlot.staffMember?._id || '',
    },
  });

  const disabled = watch('disabled');
  const staffMember = watch('staffMember');
  const daysOfWeek = watch('daysOfWeek');

  const onSubmit = async (data: FormData) => {
    try {
      const updateData: any = {
        startTime: data.startTime,
        endTime: data.endTime,
        maxParticipants: data.maxParticipants,
        disabled: data.disabled,
      };

      // Handle repeatability: specific date takes priority
      if (data.date) {
        updateData.date = data.date;
        updateData.applyEveryDay = false;
        updateData.daysOfWeek = undefined;
      } else if (data.daysOfWeek && data.daysOfWeek.length > 0) {
        // If all 7 days selected, use applyEveryDay instead
        if (data.daysOfWeek.length === 7) {
          updateData.applyEveryDay = true;
          updateData.daysOfWeek = undefined;
        } else {
          updateData.daysOfWeek = data.daysOfWeek;
          updateData.applyEveryDay = false;
        }
        updateData.date = undefined;
      } else {
        updateData.applyEveryDay = true;
        updateData.date = undefined;
        updateData.daysOfWeek = undefined;
      }

      // Handle staff member
      if (data.staffMember) {
        updateData.staffMember = data.staffMember;
      } else {
        updateData.staffMember = null;
      }

      await updateTimeSlotMutation.mutateAsync({
        classId: gymClass._id,
        timeSlotId,
        data: updateData,
      });

      toast({
        title: t('common.success'),
        description: t('classes.timeSlotUpdated'),
      });
      onOpenChange(false);
    } catch (error: any) {
      toast({
        title: t('common.error'),
        description: error.response?.data?.message || t('common.errorOccurred'),
        variant: 'destructive',
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('classes.editTimeSlot')}</DialogTitle>
          <DialogDescription>
            {gymClass.name} - {gymClass.label}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="startTime">{t('classes.startTime')}</Label>
            <Input id="startTime" type="time" {...register('startTime')} />
          </div>

          <div className="space-y-2">
            <Label htmlFor="endTime">{t('classes.endTime')}</Label>
            <Input id="endTime" type="time" {...register('endTime')} />
          </div>

          <div className="space-y-2">
            <Label htmlFor="staffMember">
              {t('classes.instructor', 'Instructor')} ({t('common.optional', 'Optional')})
            </Label>
            <StaffSelector
              value={staffMember || ''}
              onChange={(value) => setValue('staffMember', value)}
              allowClear
              placeholder={t('classes.useClassDefault', 'Use class default')}
            />
            <p className="text-sm text-muted-foreground">
              {t('classes.instructorOverrideHint', 'Leave empty to use class default instructor')}
            </p>
          </div>

          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label>
                {t('classes.daysOfWeek', 'Days of Week')}
              </Label>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => {
                  const currentDays = daysOfWeek || [];
                  if (currentDays.length === 7) {
                    setValue('daysOfWeek', []);
                  } else {
                    setValue('daysOfWeek', [0, 1, 2, 3, 4, 5, 6]);
                  }
                }}
              >
                {(daysOfWeek || []).length === 7
                  ? t('classes.clearAll', 'Clear All')
                  : t('classes.selectAll', 'Select All')}
              </Button>
            </div>
            <DaysOfWeekSelector
              value={daysOfWeek || []}
              onChange={(days) => setValue('daysOfWeek', days)}
            />
            <p className="text-sm text-muted-foreground">
              {(daysOfWeek || []).length === 7
                ? t('classes.allDaysSelected', 'All days selected (everyday)')
                : t('classes.selectDaysHint', 'Select days when this slot is available')}
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="date">
              {t('classes.specificDate', 'Specific Date')} ({t('common.optional', 'Optional')})
            </Label>
            <Input id="date" type="date" {...register('date')} />
            <p className="text-sm text-muted-foreground">
              {t('classes.specificDateNote', 'If set, this slot will only be available on this specific date')}
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="maxParticipants">
              {t('classes.maxParticipants')}
            </Label>
            <Input
              id="maxParticipants"
              type="number"
              min={timeSlot.bookedCount || 1}
              {...register('maxParticipants', { valueAsNumber: true })}
            />
            {timeSlot.bookedCount && timeSlot.bookedCount > 0 && (
              <p className="text-sm text-muted-foreground">
                {t('classes.bookedCount')}: {timeSlot.bookedCount}
              </p>
            )}
          </div>

          <div className="flex items-center space-x-2">
            <Checkbox
              id="disabled"
              checked={disabled}
              onCheckedChange={(checked) =>
                setValue('disabled', checked as boolean)
              }
            />
            <Label htmlFor="disabled" className="cursor-pointer">
              {t('classes.disabled')}
            </Label>
          </div>

          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={updateTimeSlotMutation.isPending}>
              {updateTimeSlotMutation.isPending
                ? t('common.saving')
                : t('common.save')}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
